// Statut d'un numéro de TVA (badge « VIES »), partagé par la fiche contact
// (édition admin et broker) et la vue contact du broker. Le contrôle lui-même
// est côté serveur (helpers/vies.js) : VIES pour l'UE, clé de contrôle pour la
// Roumanie.
//
// Chaque écran avait sa copie de `checkVies`, qui ne lisait que `isValid` :
//  - une fiche SANS numéro demandait /vieschecker/null et affichait « NOT VALID » ;
//  - un contrôle en échec laissait le badge précédent : `this` est undefined dans
//    le `.catch(function () { … })` d'origine, l'affectation plantait ;
//  - rien ne distinguait un numéro refusé d'un VIES en panne.
// Une seule copie désormais, sur cinq statuts :
//    valid · invalid · unavailable · not_applicable · empty
//
// Aussi :
//  - le numéro part en query string : dans le chemin, une barre cassait l'URL ;
//  - les demandes partent au chargement, à la sortie du champ et après le
//    contrôle ANAF, et peuvent revenir dans le désordre : seule la réponse à la
//    DERNIÈRE demande est retenue ;
//  - un appel qui échoue s'affiche « unavailable », jamais « NOT VALID ».
import axios from '@/axios.js'

const emptyVies = () => ({ status: 'empty', message: '', name: null, vat_number: '' })

export default {
  data () {
    return {
      vies: emptyVies(),
      viesChecking: false,
      viesRequestSeq: 0
    }
  },
  computed: {
    // `dark` (gris) pour les états sans verdict : `neutral` n'a aucune règle CSS dans
    // Vuesax, le badge deviendrait un simple texte au milieu des autres.
    viesBadge () {
      if (this.viesChecking) return { color: 'dark', icon: 'LoaderIcon', text: 'Checking VIES…' }
      if (this.vies.status === 'valid') return { color: 'primary', icon: 'ThumbsUpIcon', text: 'VIES → VALID' }
      if (this.vies.status === 'invalid') return { color: 'danger', icon: 'ThumbsDownIcon', text: 'VIES → NOT VALID' }
      if (this.vies.status === 'unavailable') return { color: 'warning', icon: 'AlertCircleIcon', text: 'VIES unavailable — retry later' }
      if (this.vies.status === 'not_applicable') return { color: 'dark', icon: 'ZapOffIcon', text: 'Not checked in VIES' }
      return { color: 'dark', icon: 'ZapOffIcon', text: 'No VAT number' }
    },
    // Ligne sous le badge : le motif donné par le serveur, ou le nom que VIES
    // associe au numéro (un numéro valide peut être celui d'une autre société).
    viesNote () {
      if (this.viesChecking) return ''
      if (this.vies.status === 'valid') return this.vies.name ? `Registered in VIES as: ${this.vies.name}` : ''
      return this.vies.message || ''
    },
    // Rouge : la fiche ne s'enregistrera pas. Orange : VIES en panne, réessayer.
    viesNoteClass () {
      if (this.vies.status === 'invalid') return 'text-danger'
      if (this.vies.status === 'unavailable') return 'text-warning'
      return 'text-grey'
    }
  },
  methods: {
    checkVies (vatNumber) {
      const seq = this.viesRequestSeq + 1
      this.viesRequestSeq = seq
      const vat = vatNumber === null || vatNumber === undefined ? '' : String(vatNumber).trim()
      if (!vat) {
        this.viesChecking = false
        this.vies = emptyVies()
        return
      }
      this.viesChecking = true
      axios.get('/vieschecker', { params: { vat } })
        .then((response) => {
          if (seq !== this.viesRequestSeq) return
          this.viesChecking = false
          const data = response.data
          this.vies = data && data.status ? data : emptyVies()
        })
        .catch(() => {
          if (seq !== this.viesRequestSeq) return
          this.viesChecking = false
          this.vies = {
            status: 'unavailable',
            message: 'The VAT check could not be reached. Try again in a few minutes.',
            name: null,
            vat_number: vat
          }
        })
    },
    // Un CNP (13 chiffres, personne physique) tapé dans « VAT Number » (Chady,
    // 2026-11-03, fiche 5628) : ce n'est pas un numéro de TVA. À la sortie du champ,
    // la fiche le dit (boîte de dialogue, EN + RO) et le range elle-même dans
    // CUI / CNP — la colonne que SAGA lit comme cod fiscal et que l'e-Factura
    // traite comme personne physique. Le serveur applique la même règle
    // (helpers/cnpInVat.js) pour les autres chemins d'écriture.
    cnpInVatNumber (value) {
      const s = String(value === null || value === undefined ? '' : value).replace(/[\s.-]/g, '').toUpperCase()
      const m = /^(?:RO)?(\d{13})$/.exec(s)
      return m ? m[1] : null
    },
    // Rend true quand le champ portait un CNP : le contrôle VIES n'a plus rien
    // à regarder. Si CUI / CNP porte déjà un AUTRE numéro, rien ne bouge : la
    // boîte le dit, et le serveur refusera la fiche telle quelle (422 cnp_in_vat).
    moveCnpOutOfVat () {
      const p = this.data_local
      if (!p) return false
      const cnp = this.cnpInVatNumber(p.vat_number)
      if (!cnp) return false
      const cuiDigits = String(p.cui === null || p.cui === undefined ? '' : p.cui).replace(/\D/g, '')
      const conflict = !!cuiDigits && cuiDigits !== cnp
      if (!conflict) {
        p.vat_number = ''
        p.cui = cnp
        p.subject_vat = false
        this.viesRequestSeq = this.viesRequestSeq + 1
        this.viesChecking = false
        this.vies = emptyVies()
        if (typeof this.checkAnafVat === 'function') this.checkAnafVat()
      }
      const en = `${cnp} has 13 digits: it is the national register number (CNP) of a private person, not a VAT number.`
      const ro = `RO: ${cnp} este un CNP (13 cifre), nu un cod de TVA.`
      const whenConflict = `${en} It belongs in CUI / CNP, but that field already holds ${cuiDigits}: keep one of the two. ${ro} Locul lui este în CUI / CNP, unde există deja ${cuiDigits}: păstrați unul dintre cele două.`
      const whenMoved = `${en} It has been moved to CUI / CNP and VAT Number is now empty — save to keep it. ${ro} A fost mutat în CUI / CNP, iar VAT Number a rămas gol — salvați.`
      this.$vs.dialog({
        type: 'alert',
        color: 'warning',
        title: 'This is a CNP, not a VAT number',
        text: conflict ? whenConflict : whenMoved,
        acceptText: 'OK'
      })
      return true
    },
    // Enregistrement refusé pour le numéro de TVA : le badge reprend le statut
    // que le serveur vient d'établir, sans redemander.
    applyViesRefusal (data) {
      if (!data || !data.vat_check || !data.vat_check.status) return
      this.viesRequestSeq = this.viesRequestSeq + 1
      this.viesChecking = false
      this.vies = data.vat_check
    },
    // Échec d'enregistrement de la fiche (écrans admin et broker).
    // Refus pour le numéro de TVA → boîte de dialogue à fermer soi-même : une
    // notification s'efface en 12 s et passait inaperçue. Le motif reste aussi
    // écrit sous le champ TVA. Un 503 sans corps lisible (page d'erreur d'un
    // proxy) est traité comme une panne : on ne sait pas qui ne répond pas.
    showContactSaveFailure (failure) {
      console.error('contact save:', failure)
      const response = failure && failure.response
      const data = response && response.data && typeof response.data === 'object' ? response.data : null
      this.applyViesRefusal(data)
      const code = data && data.code
      const outage = code === 'vies_unavailable' || (!code && response && response.status === 503)
      if (code === 'vat_invalid' || outage) {
        this.$vs.dialog({
          type: 'alert',
          color: outage ? 'warning' : 'danger',
          title: outage ? 'Contact NOT saved — VIES unavailable' : 'Contact NOT saved — VAT number not valid',
          text: (data && data.message) ||
            'The server or VIES (the EU VAT number service) is not answering right now. The contact was NOT saved. Try again later.',
          acceptText: 'OK'
        })
        return
      }
      this.$vs.notify({
        color: 'danger',
        title: 'Contact NOT saved',
        text: (data && (data.message || data.error)) ||
          (response
            ? 'The server refused the change.'
            : 'No answer from the server. The contact may not have been saved: reload the page and check.'),
        time: 12000
      })
    }
  }
}
